
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { Organization } from '../../../../core/models/model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BuildingCameraComponent } from '../../building-camera/building-camera.component';
import { DetailsModalComponent } from '../details-modal/details-modal.component';
import { GlobalSettingsService } from '../../../../core/services/global-settings.service';
import { MultipleMessageComponent } from '../multiple-message/multiple-message.component';
import _ from 'lodash';
import { OrganizationService } from '../../../../core/services/organization.service';
import { User } from '../../../../core/models/organization.interface';

@Component({
  selector: 'app-org-pane',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './org-pane.component.html',
  styleUrl: './org-pane.component.css'
})
export class OrgPaneComponent implements OnInit, OnChanges {
  @Input() organizations: any[] = [];
  @Output() orgSelected = new EventEmitter<Organization>();
  @Output() orgInfoRequested = new EventEmitter<Organization>();
  @Output() orgMessageRequested = new EventEmitter<Organization>();
  filteredOrganizations: Organization[] = [];
  searchQuery = '';
  @Input() users: User[] = [];
  private searchSubject = new Subject<string>();
  constructor(private modalService: NgbModal,  private globalSettings: GlobalSettingsService, private organizationService: OrganizationService) {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(query => {
        this.filterOrganizations(query);
      });
  }

  ngOnInit(): void {
    this.filteredOrganizations = this.sortOrganizations(this.organizations);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizations']) {
      this.filteredOrganizations = this.sortOrganizations(this.organizations);
    }
  }
  private sortOrganizations(orgs: any[]): Organization[] {
    return [...orgs].sort((a, b) => 
      (a.name?.toLowerCase() || '').localeCompare(b.name?.toLowerCase() || '')
    );
  }

  onSearch(query: string): void {
    this.searchSubject.next(query);
  }

  filterOrganizations(query: string): void {
    if (!query) {
      this.filteredOrganizations = this.sortOrganizations(this.organizations);
      return;
    }
    this.filteredOrganizations = this.sortOrganizations(
      this.organizations.filter(org => 
        org.name?.toLowerCase().includes(query.toLowerCase()) ?? false
      )
    );
  }

  onOrganizationSelect(org: Organization): void {
    this.orgSelected.emit(org);
  }

  showOrganizationInfo(org: Organization, event: Event) {
    event.stopPropagation();
    // Open the details modal with the organization data
    const title = 'Organization Details';  
    this.openDetailsModal(title, org, true);    
    this.orgInfoRequested.emit(org);
  }

  sendMessage(org: any, event: Event) {
     const options = {
        bulkOrg: true,
				orgId: org.organization.id,
				orgName: org.organization.name,
				recipientCount: this.getOrgUserCount(org)
    };
    this.openMultipleMessageModalWithOptions('Send Message', options);
  }

  private getOrgUserCount(org: any): number {
    if (!this.users || this.users.length === 0) return 0;

    // Determine actual org ID and descendants
    const orgId = org?.organization?.id ?? org?.id; // fallback if org is already Organization
    const descendants = org?.organization?.descendants ?? org?.descendants ?? [];

    // Ensure all org IDs are strings
    const orgIds = _.concat([String(orgId)], descendants.map(String));

    // Filter users whose orgIds intersect with this organization
    const count = _.filter(this.users, (user: any) => {
      // Convert user's orgIds to strings for consistent comparison
      return _.intersection(orgIds, (user.orgIds || []).map(String)).length > 0;
    }).length;

    return count;
  }

  private openMultipleMessageModalWithOptions(title: string, options: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(MultipleMessageComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.options = options;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }
 getNumberOfUsersFiltered(): number {
    return this.globalSettings.getNumberOfUsersFiltered();
  }
  onOrgSelected(org: Organization): void {
    const modalRef = this.modalService.open(BuildingCameraComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.entity = org; // or pass the relevant building data
  }

  private openDetailsModal(title: string, org:any, shouldCallApi: boolean ) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.org = org;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;
    return modalRef.result;
  }
    getNumberOfOrganisationFilter(): number {
    return this.globalSettings.getNumberOfOrganisationFilter();
  }
}